import type {
	Fetch,
	FetchOptions,
	Middleware,
	PipelineBuilder,
} from "@bokio/mobile-web-shared/services/api/client/AppMiddleware";

export type { Fetch, FetchOptions, Middleware, PipelineBuilder };

interface AppMiddleware {
	fetch: Fetch;

	setupPipeline: (builder: (builder: PipelineBuilder) => void) => void;
}

/**
 * NE 2019-09-25
 * The app mutates this middleware with a new fetch function
 * to intercept requests and reponses to/from the backend.
 **/

export const appMiddleware: AppMiddleware = {
	fetch: (input, options) => window.fetch(input, options as RequestInit),

	/**
	 *
	 * @param builder A function that receives a PipelineBuilder to which middlewares can be added.
	 *
	 * Usage: appMiddleware.setupPipeline(builder => {
	 *            builder.add(middleware1);
	 *            builder.add(middleware2);
	 *        });
	 * This example will have middleware1 first to be called on fetch() invocation. If middleware1 calls next(), then middleware2 will be called next, and so on.
	 */
	setupPipeline: builder => {
		const middlewares: Middleware[] = [];

		const pipelineBuilder: PipelineBuilder = {
			add: (middleware: Middleware) => {
				middlewares.push(middleware);
			},
		};
		builder(pipelineBuilder);
		// Use a lazy wrapper so the pipeline always resolves window.fetch at call time.
		// Using .bind() would freeze the reference, breaking tests that mock window.fetch after setup.
		let currentFetch: Fetch = (input, options) => window.fetch(input, options as RequestInit);
		for (let i = middlewares.length - 1; i >= 0; i--) {
			currentFetch = middlewares[i](currentFetch);
		}
		appMiddleware.fetch = currentFetch;
	},
};
